import { Link } from '@tanstack/react-router'
import { Briefcase, CircleDot, X } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardBody } from '@/components/ui/card'
import { Dialog } from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { ApplicationStatusBadge, applicationLabel } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useApplication, useMyApplications, useWithdrawApplication } from '@/features/hiring/api'
import { formatDateTime, formatRelative, formatRupiah } from '@/lib/format'
import { cn } from '@/lib/cn'
import type { ApplicationState } from '@/lib/types'

/** Urutan tahap yang ditampilkan sebagai linimasa pada rincian lamaran. */
const pipeline: ApplicationState[] = ['submitted', 'reviewed', 'shortlisted', 'interview', 'offered', 'hired']

export function ApplicationsPage() {
  const { data, isLoading } = useMyApplications()
  const [detailId, setDetailId] = useState<string | null>(null)
  const withdraw = useWithdrawApplication()
  const toast = useToast()

  const applications = data?.applications ?? []

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">Lamaran saya</h1>
          <p className="mt-1 text-muted">Pantau tahap seleksi setiap lamaran yang Anda kirim.</p>
        </div>
        <Link to="/lowongan">
          <Button variant="outline">Cari lowongan</Button>
        </Link>
      </header>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} className="h-28 rounded-2xl" />
          ))}
        </div>
      ) : applications.length === 0 ? (
        <Card>
          <EmptyState illustration="box"
            icon={<Briefcase className="size-6" />}
            title="Belum ada lamaran"
            description="Lamaran yang Anda kirim akan tampil di sini beserta tahap seleksinya."
            action={
              <Link to="/lowongan">
                <Button>Lihat lowongan</Button>
              </Link>
            }
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {applications.map((application) => (
            <Card key={application.id}>
              <CardBody className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ApplicationStatusBadge state={application.state} />
                    <span className="text-xs text-subtle">
                      Dilamar {formatRelative(application.applied_at)}
                    </span>
                  </div>
                  <h2 className="mt-2 font-bold text-ink">{application.vacancy.title}</h2>
                  <p className="mt-0.5 text-sm text-muted">{application.vacancy.company_name}</p>
                  {application.state_note ? (
                    <p className="mt-2 rounded-lg bg-sunken px-3 py-2 text-sm text-muted">
                      “{application.state_note}”
                    </p>
                  ) : null}
                </div>

                <div className="flex shrink-0 flex-col items-end gap-2">
                  {application.expected_salary ? (
                    <p className="text-sm text-muted">
                      Ekspektasi {formatRupiah(application.expected_salary)}
                    </p>
                  ) : null}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setDetailId(application.id)}>
                      Lihat tahapan
                    </Button>
                    {application.next_states.length > 0 ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-danger"
                        loading={withdraw.isPending}
                        onClick={() => {
                          if (!window.confirm('Tarik lamaran ini?')) return
                          withdraw.mutate(application.id, {
                            onSuccess: () => toast.info('Lamaran ditarik'),
                            onError: () => toast.error('Gagal menarik lamaran'),
                          })
                        }}
                      >
                        <X className="size-4" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <ApplicationTimeline applicationId={detailId} onClose={() => setDetailId(null)} />
    </div>
  )
}

function ApplicationTimeline({
  applicationId,
  onClose,
}: {
  applicationId: string | null
  onClose: () => void
}) {
  const { data: application } = useApplication(applicationId)

  const currentIndex = application ? pipeline.indexOf(application.state) : -1
  const closed = application?.state === 'rejected' || application?.state === 'withdrawn'

  return (
    <Dialog
      open={applicationId !== null}
      onClose={onClose}
      title={application?.vacancy.title ?? 'Tahapan lamaran'}
      description={application?.vacancy.company_name}
    >
      {application ? (
        <div className="space-y-6">
          {/* Linimasa memperlihatkan seluruh tahap, termasuk yang belum
              dilalui, supaya pelamar tahu masih ada berapa langkah lagi. */}
          <ol className="space-y-3">
            {pipeline.map((state, index) => {
              const reached = currentIndex >= index && !closed
              const isCurrent = application.state === state

              return (
                <li key={state} className="flex items-start gap-3">
                  <span
                    className={cn(
                      'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-bold',
                      reached ? 'bg-brand text-white' : 'bg-sunken text-subtle',
                    )}
                  >
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <p className={cn('text-sm', isCurrent ? 'font-bold text-ink' : 'font-medium text-muted')}>
                      {applicationLabel(state)}
                    </p>
                    {isCurrent ? (
                      <p className="mt-0.5 text-xs text-brand">Tahap Anda saat ini</p>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ol>

          {closed ? (
            <div className="rounded-xl bg-sunken p-4">
              <p className="text-sm font-bold text-ink">
                {application.state === 'rejected' ? 'Tidak dilanjutkan' : 'Lamaran ditarik'}
              </p>
              {application.state_note ? (
                <p className="mt-1 text-sm text-muted">{application.state_note}</p>
              ) : null}
            </div>
          ) : null}

          <div>
            <p className="text-sm font-semibold text-ink">Riwayat</p>
            <ul className="mt-2 space-y-2">
              {application.events?.map((event) => (
                <li key={event.id} className="flex items-start gap-3 rounded-xl border border-line p-3">
                  <CircleDot className="mt-0.5 size-4 shrink-0 text-brand" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-ink">{applicationLabel(event.to_state as ApplicationState)}</p>
                      <span className="text-xs text-subtle">{formatDateTime(event.created_at)}</span>
                    </div>
                    {event.note ? <p className="mt-0.5 text-sm text-muted">{event.note}</p> : null}
                    <p className="mt-0.5 text-xs text-subtle">oleh {event.actor_name}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <Skeleton className="h-64" />
      )}
    </Dialog>
  )
}
